import { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  signUp: (email: string, password: string, nickname?: string, cdkCode?: string) => Promise<{ error: Error | null; userId?: string }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setIsLoading(false);
      }
    );

    // Then get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string, nickname?: string, cdkCode?: string) => {
    const normalizedEmail = email.trim().toLowerCase();
    try {
      const { data, error } = await supabase.auth.signUp({
        email: normalizedEmail,
        password,
        options: {
          emailRedirectTo: window.location.origin,
          data: {
            nickname: nickname || normalizedEmail.split('@')[0],
            cdk_code: cdkCode,
          },
        },
      });
      
      if (error) throw error;
      
      // If signup succeeded and we have a CDK code, mark it as used
      if (data.user && cdkCode) {
        await supabase.rpc('validate_and_use_cdk', {
          _code: cdkCode,
          _user_id: data.user.id
        });
      }
      
      return { error: null, userId: data.user?.id };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signIn = async (email: string, password: string) => {
    const normalizedEmail = email.trim().toLowerCase();
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password,
      });
      
      if (error) throw error;
      
      // 登录成功后注册设备
      const deviceInfo = getDeviceInfo();
      
      // 使用 try-catch 包装设备注册，不影响登录流程
      try {
        await supabase.functions.invoke("register-device", {
          body: {
            device_name: deviceInfo.name,
            browser: deviceInfo.browser,
            os: deviceInfo.os,
          },
        });
      } catch (deviceError) {
        // 设备注册失败不影响登录
        console.warn("Device registration failed:", deviceError);
      }
      
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

function getDeviceInfo() {
  const ua = navigator.userAgent;
  let browser = "未知浏览器";
  let os = "未知系统";
  let name = "未知设备";

  if (ua.includes("Chrome") && !ua.includes("Edg")) browser = "Chrome";
  else if (ua.includes("Safari") && !ua.includes("Chrome")) browser = "Safari";
  else if (ua.includes("Firefox")) browser = "Firefox";
  else if (ua.includes("Edg")) browser = "Edge";

  if (ua.includes("Windows")) { os = "Windows"; name = "Windows 电脑"; }
  else if (ua.includes("Mac")) { os = "macOS"; name = "Mac 电脑"; }
  else if (ua.includes("iPhone")) { os = "iOS"; name = "iPhone"; }
  else if (ua.includes("iPad")) { os = "iPadOS"; name = "iPad"; }
  else if (ua.includes("Android")) { 
    os = "Android"; 
    name = ua.includes("Mobile") ? "Android 手机" : "Android 平板"; 
  }
  else if (ua.includes("Linux")) { os = "Linux"; name = "Linux 电脑"; }

  return { name: `${name} - ${browser}`, browser, os };
}

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, isLoading, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
